import React, { useState, useRef, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AudioPlaybackProps {
  duration: string;
  audioUrl?: string;
}

const AudioPlayback: React.FC<AudioPlaybackProps> = ({
  duration,
  audioUrl,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<number>();

  useEffect(() => {
    // Reset error state when audio URL changes
    setError(null);
    
    // Create audio element
    if (audioUrl && !audioRef.current) {
      audioRef.current = new Audio(audioUrl);

      // Add event listeners
      audioRef.current.addEventListener("ended", () => {
        setIsPlaying(false);
        setProgress(0);
      });

      audioRef.current.addEventListener("pause", () => {
        setIsPlaying(false);
      });

      audioRef.current.addEventListener("play", () => {
        setIsPlaying(true);
      });
      
      // Add error event listener to detect blocked resources
      audioRef.current.addEventListener("error", (e) => {
        console.error("Audio loading error:", e);
        setIsPlaying(false);
        setError("Failed to load audio. It may be blocked by your browser or extensions.");
      });
    }

    return () => {
      // Cleanup
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [audioUrl]);

  const togglePlayback = () => {
    if (!audioRef.current || !audioUrl) return;

    if (isPlaying) {
      audioRef.current.pause();
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    } else {
      // Reset error state before attempting to play
      setError(null);
      
      audioRef.current.play()
        .then(() => {
          // Update progress every 100ms
          progressIntervalRef.current = window.setInterval(() => {
            if (audioRef.current) {
              setProgress(
                (audioRef.current.currentTime / audioRef.current.duration) * 100
              );
            }
          }, 100);
        })
        .catch((error) => {
          console.error("Audio playback error:", error);
          // Reset playing state in case of error
          setIsPlaying(false);
          
          // Set user-friendly error message
          if (error instanceof DOMException && error.name === "NotSupportedError") {
            setError("Audio playback failed. The audio source may be blocked by your browser or extensions.");
          } else {
            setError(`Audio playback error: ${error instanceof Error ? error.message : String(error)}`);
          }
        });
    }
  };

  return (
    <div className="mt-4">
      <h3 className="font-medium text-gray-700 mb-2">Audio Playback</h3>
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="icon"
          className="rounded-full h-12 w-12 p-0 border-gray-300"
          onClick={togglePlayback}
          disabled={!audioUrl}
        >
          {isPlaying ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="h-5 w-5" />
          )}
        </Button>
        <div className="bg-gray-200 h-2 flex-1 rounded-full">
          <div
            className="h-full bg-gray-400 rounded-full transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-sm text-gray-500">{duration}</span>
      </div>
      {!audioUrl && (
        <p className="text-sm text-gray-500 mt-2">
          Audio recording not available
        </p>
      )}
      {error && (
        <p className="text-sm text-red-500 mt-2">
          {error}
        </p>
      )}
    </div>
  );
};

export default AudioPlayback;
